import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Clock, User, Mail, Trash2, Archive, AlertCircle, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';
import { inquiriesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminInquiriesArchive() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearDays, setClearDays] = useState(30);

  const loadArchived = useCallback(async () => {
    try {
      const response = await inquiriesApi.getArchivedInquiries();
      setInquiries(response.data || []);
    } catch (err) {
      console.error('Error loading archived inquiries:', err);
      toast.error('Fehler beim Laden des Archivs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArchived();
  }, [loadArchived]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await inquiriesApi.delete(deleteId);
      setInquiries(inquiries.filter(i => i.id !== deleteId));
      toast.success('Anfrage gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const handleClearOld = async () => {
    try {
      const result = await inquiriesApi.clearArchivedInquiries(clearDays);
      toast.success(`${result.data?.deleted_count || 0} alte Anfragen gelöscht`);
      setShowClearDialog(false);
      loadArchived();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'replied':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded"><Check size={12} /> Beantwortet</span>;
      case 'new':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded"><Clock size={12} /> Neu</span>;
      default:
        return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header with Back Button */}
      <div className="mb-6">
        <Link 
          to="/admin/inquiries" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft size={18} />
          Zurück zu Anfragen
        </Link>
        
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-3">
              <Archive className="text-muted-foreground" size={28} />
              Anfragen-Archiv
            </h1>
            <p className="text-muted-foreground mt-1">
              {inquiries.length} archivierte Anfragen (älter als 14 Tage)
            </p>
          </div>
          
          {inquiries.length > 0 && (
            <Button
              onClick={() => setShowClearDialog(true)}
              variant="outline"
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 size={16} />
              Alte Einträge löschen
            </Button>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertCircle size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <strong>Info:</strong> Hier werden alle Kundenanfragen angezeigt, die älter als 14 Tage sind. 
          Diese werden automatisch archiviert.
        </div>
      </div>

      {/* Archived Inquiries List */}
      {inquiries.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Archive size={48} className="mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">Keine archivierten Anfragen</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 font-medium text-sm">Datum</th>
                  <th className="text-left p-4 font-medium text-sm">Kunde</th>
                  <th className="text-left p-4 font-medium text-sm">Nachricht</th>
                  <th className="text-left p-4 font-medium text-sm">Fahrzeug</th>
                  <th className="text-left p-4 font-medium text-sm">Status</th>
                  <th className="text-center p-4 font-medium text-sm">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {inquiries.map((inquiry) => (
                  <tr key={inquiry.id} className="hover:bg-muted/30 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock size={14} className="text-muted-foreground" />
                        <span>{formatDate(inquiry.created_at)}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-muted-foreground" />
                        <span className="font-medium">{inquiry.customer_name}</span>
                      </div>
                      <a 
                        href={`mailto:${inquiry.customer_email}`}
                        className="flex items-center gap-1 text-sm text-primary hover:underline mt-1"
                      >
                        <Mail size={12} />
                        {inquiry.customer_email}
                      </a>
                    </td>
                    <td className="p-4 max-w-xs">
                      <p className="text-sm text-muted-foreground truncate">
                        {inquiry.message}
                      </p>
                    </td>
                    <td className="p-4">
                      {inquiry.car_info ? (
                        <span className="text-sm">
                          {inquiry.car_info.brand} {inquiry.car_info.model}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      {getStatusBadge(inquiry.status)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-2">
                        <Link
                          to={`/admin/inquiries/${inquiry.id}`}
                          className="p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          title="Details anzeigen"
                        >
                          <ExternalLink size={16} />
                        </Link>
                        <button
                          onClick={() => setDeleteId(inquiry.id)}
                          className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Single Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anfrage löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diese archivierte Anfrage löschen möchten? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Old Inquiries Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alte Einträge löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Alle archivierten Anfragen löschen, die älter als die angegebene Anzahl an Tagen sind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium mb-2">
              Anfragen älter als (Tage):
            </label>
            <input
              type="number"
              value={clearDays}
              onChange={(e) => setClearDays(Math.max(14, parseInt(e.target.value) || 30))}
              min={14}
              className="w-full border border-border rounded-lg px-3 py-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearOld} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
